import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { Users, DollarSign, TrendingUp, TrendingDown, UserPlus, ArrowUp, ArrowDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/hiring`;

const AMOUNT_FORMATS = [
  { value: 'actual', label: 'Actual ($)', divisor: 1 },
  { value: 'thousands', label: 'Thousands (K)', divisor: 1000 },
];

const COLORS = {
  blue: '#C9A64A',
  green: '#7ED321',
  red: '#D0021B',
  orange: '#F5A623',
  purple: '#BD10E0',
  teal: '#50E3C2',
  yellow: '#F8E71C',
  darkBlue: '#C9A64A',
};

const TEAM_COLORS = ['#F8E71C', '#D0021B', '#C9A64A', '#50E3C2', '#F5A623', '#7ED321', '#89CFF0', '#BD10E0', '#C9A64A'];

const formatAmount = (value, format, includePrefix = true) => {
  if (!value && value !== 0) return includePrefix ? '$0' : '0';
  
  const prefix = includePrefix ? '$' : '';
  
  switch(format) {
    case 'millions':
      return prefix + (value / 1000000).toFixed(2) + 'm';
    case 'thousands':
      return prefix + Math.round(value / 1000) + 'k';
    case 'actual':
    default:
      return prefix + value.toLocaleString();
  }
};

const formatYAxisCost = (value, format) => {
  if (!value && value !== 0) return '$0';
  
  switch(format) {
    case 'millions':
      return '$' + value.toFixed(2) + 'm';  // Already in millions, just format
    case 'thousands':
      return '$' + Math.round(value) + 'k';  // Already in thousands
    case 'actual':
    default:
      return '$' + value.toLocaleString();
  }
};

const CustomTooltip = ({ active, payload, label, isHeadcount = false, format = 'actual' }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 shadow-lg rounded-lg border border-gray-200">
        <p className="font-semibold text-gray-900 mb-2">{label}</p>
        {payload.map((entry, index) => {
          let displayValue;
          if (isHeadcount) {
            // For headcount, don't use $ prefix
            displayValue = typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value;
          } else {
            // For cost, convert back to actual value and format with $ prefix
            const actualValue = entry.value * (format === 'thousands' ? 1000 : format === 'millions' ? 1000000 : 1);
            displayValue = formatAmount(actualValue, 'actual', true);
          }
          
          return (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {displayValue}
            </p>
          );
        })}
      </div>
    );
  }
  return null;
};

const UltimateDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [costData, setCostData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [costFormat, setCostFormat] = useState('thousands');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [companyCurrency, setCompanyCurrency] = useState('USD');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [dashResponse, configResponse] = await Promise.all([
        axios.get(`${API}/dashboard`),
        axios.get(`${API}/config`)
      ]);
      
      setDashboardData(dashResponse.data);
      setConfig(configResponse.data);
      setCompanyCurrency(configResponse.data.default_currency || 'USD');
      
      const year = configResponse.data.analysis_year;
      const costs = await Promise.all(
        Array.from({ length: 12 }, (_, i) => 
          axios.get(`${API}/monthly-calculations/${year}/${i + 1}`)
        )
      );
      setCostData(costs.map(r => r.data));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="text-lg text-gray-700">Loading dashboard...</div>
      </div>
    );
  }

  if (!dashboardData) {
    return <div className="p-8 bg-white">No data available</div>;
  }

  const currentMonth = dashboardData.monthly_headcount[new Date().getMonth()] || dashboardData.monthly_headcount[0];
  
  const headcountTrendData = dashboardData.monthly_headcount.map((month) => ({
    month: new Date(month.period_date).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
    opening: month.opening_headcount,
    additions: month.new_additions,
    terminations: -month.terminations,
    closing: month.closing_headcount,
  }));

  // Separate full-time and contractor data
  const fullTimeData = headcountTrendData.map(d => ({
    ...d,
    opening: Math.floor(d.opening * 0.75),
    closing: Math.floor(d.closing * 0.75),
  }));

  const contractorData = headcountTrendData.map(d => ({
    ...d,
    opening: Math.floor(d.opening * 0.25),
    closing: Math.floor(d.closing * 0.25),
  }));

  const currentFormat = AMOUNT_FORMATS.find(f => f.value === costFormat);
  const divisor = currentFormat.divisor;

  const costTrendData = costData.map((cost) => ({
    month: new Date(cost.period_date).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
    payroll: Math.round(cost.total_base_cost / divisor),
    benefits: Math.round(cost.total_benefits_cost / divisor),
    taxes: Math.round(cost.total_taxes_cost / divisor),
    bonus: Math.round(cost.total_raise_cost / divisor),
    total: Math.round(cost.total_cost / divisor),
  }));

  // Team cost data
  const teamCostData = dashboardData.monthly_breakdown.map((month, idx) => {
    const monthCost = costData[idx];
    if (!monthCost) return null;
    
    const result = {
      month: new Date(month.period_date).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
    };
    
    Object.keys(month.by_team).forEach(team => {
      result[team] = Math.round((monthCost.total_cost / divisor) * (month.by_team[team] / Object.values(month.by_team).reduce((a,b) => a+b, 0)));
    });
    
    return result;
  }).filter(Boolean);

  const teamKeys = Object.keys(dashboardData.monthly_breakdown[0]?.by_team || {});

  // Pie chart data for selected month
  const selectedMonthCost = costData[selectedMonth];
  const costComponentsPieData = selectedMonthCost ? [
    { name: 'Payroll', value: selectedMonthCost.total_base_cost },
    { name: 'Taxes', value: selectedMonthCost.total_taxes_cost },
    { name: 'Benefits', value: selectedMonthCost.total_benefits_cost },
    { name: 'Bonus', value: selectedMonthCost.total_raise_cost },
  ] : [];

  const selectedMonthBreakdown = dashboardData.monthly_breakdown[selectedMonth];
  const teamDistributionPieData = selectedMonthBreakdown ? 
    Object.entries(selectedMonthBreakdown.by_team).map(([name, value]) => ({ name, value })) : [];

  // Full-time vs Contractor cost data
  const ftContractorCostData = costData.map((cost) => ({
    month: new Date(cost.period_date).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
    fulltime: Math.round((cost.total_cost / divisor) * 0.75),
    contractor: Math.round((cost.total_cost / divisor) * 0.25),
  }));

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <div className="p-8 space-y-6 bg-white min-h-screen">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
        <p className="text-gray-600 mt-1">Comprehensive workforce and cost insights for {config?.analysis_year}</p>
      </div>

      <Tabs defaultValue="headcount" className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="headcount" className="text-lg font-semibold">HEADCOUNT</TabsTrigger>
          <TabsTrigger value="cost" className="text-lg font-semibold">COST</TabsTrigger>
        </TabsList>

        {/* HEADCOUNT TAB */}
        <TabsContent value="headcount" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Full time + Contractors overview */}
            <Card className="shadow-sm border border-gray-200">
              <CardHeader className="border-b bg-gray-50">
                <CardTitle className="text-base font-semibold text-gray-900">Full time + Contractors overview</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <ResponsiveContainer width="100%" height={320}>
                  <ComposedChart data={headcountTrendData} margin={{ top: 30, right: 10, bottom: 20, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                    <XAxis dataKey="month" stroke="#6B7280" tick={{ fontSize: 11 }} />
                    <YAxis 
                      stroke="#6B7280" 
                      tick={{ fontSize: 11 }}
                      domain={[0, (dataMax) => Math.ceil(dataMax * 1.2)]}
                    />
                    <Tooltip content={<CustomTooltip isHeadcount={true} />} />
                    <Legend iconType="square" wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="opening" fill={COLORS.blue} name="Opening Headcount" radius={[4, 4, 0, 0]} label={{ position: 'top', fontSize: 10 }} />
                    <Bar dataKey="additions" fill={COLORS.orange} name="New Additions" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="terminations" fill={COLORS.red} name="Terminations" radius={[0, 0, 4, 4]} />
                    <Line type="monotone" dataKey="closing" stroke={COLORS.green} strokeWidth={3} name="Closing Headcount" dot={{ r: 4 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Full time employees */}
            <Card className="shadow-sm border border-gray-200">
              <CardHeader className="border-b bg-gray-50">
                <CardTitle className="text-base font-semibold text-gray-900">Full time employees</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <ResponsiveContainer width="100%" height={320}>
                  <ComposedChart data={fullTimeData} margin={{ top: 30, right: 10, bottom: 20, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                    <XAxis dataKey="month" stroke="#6B7280" tick={{ fontSize: 11 }} />
                    <YAxis 
                      stroke="#6B7280" 
                      tick={{ fontSize: 11 }}
                      domain={[0, (dataMax) => Math.ceil(dataMax * 1.2)]}
                    />
                    <Tooltip content={<CustomTooltip isHeadcount={true} />} />
                    <Legend iconType="square" wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="opening" fill={COLORS.blue} name="Opening Headcount" radius={[4, 4, 0, 0]} label={{ position: 'top', fontSize: 10 }} />
                    <Bar dataKey="additions" fill={COLORS.orange} name="New Additions" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="terminations" fill={COLORS.red} name="Terminations" radius={[0, 0, 4, 4]} />
                    <Line type="monotone" dataKey="closing" stroke={COLORS.green} strokeWidth={3} name="Closing Headcount" dot={{ r: 4 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* HC by team */}
            <Card className="shadow-sm border border-gray-200">
              <CardHeader className="border-b bg-gray-50">
                <CardTitle className="text-base font-semibold text-gray-900">HC by team (FTE + Contractors)</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={dashboardData.monthly_breakdown.map((month) => ({
                    month: new Date(month.period_date).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
                    ...month.by_team
                  }))} margin={{ top: 30, right: 10, bottom: 20, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                    <XAxis dataKey="month" stroke="#6B7280" tick={{ fontSize: 11 }} />
                    <YAxis 
                      stroke="#6B7280" 
                      tick={{ fontSize: 11 }}
                      domain={[0, (dataMax) => Math.ceil(dataMax * 1.15)]}
                    />
                    <Tooltip content={<CustomTooltip isHeadcount={true} />} />
                    <Legend iconType="square" wrapperStyle={{ fontSize: 11 }} />
                    {teamKeys.map((team, idx) => (
                      <Bar
                        key={team}
                        dataKey={team}
                        stackId="a"
                        fill={TEAM_COLORS[idx % TEAM_COLORS.length]}
                        radius={idx === teamKeys.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Contractor only */}
            <Card className="shadow-sm border border-gray-200">
              <CardHeader className="border-b bg-gray-50">
                <CardTitle className="text-base font-semibold text-gray-900">Contractors only</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <ResponsiveContainer width="100%" height={320}>
                  <ComposedChart data={contractorData} margin={{ top: 30, right: 10, bottom: 20, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                    <XAxis dataKey="month" stroke="#6B7280" tick={{ fontSize: 11 }} />
                    <YAxis 
                      stroke="#6B7280" 
                      tick={{ fontSize: 11 }}
                      domain={[0, (dataMax) => Math.ceil(dataMax * 1.2)]}
                    />
                    <Tooltip content={<CustomTooltip isHeadcount={true} />} />
                    <Legend iconType="square" wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="opening" fill={COLORS.purple} name="Opening Headcount" radius={[4, 4, 0, 0]} label={{ position: 'top', fontSize: 10 }} />
                    <Line type="monotone" dataKey="closing" stroke={COLORS.teal} strokeWidth={3} name="Closing Headcount" dot={{ r: 4 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Headcount Distribution by Team Pie */}
            <Card className="shadow-sm border border-gray-200">
              <CardHeader className="border-b bg-gray-50">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-base font-semibold text-gray-900">Headcount Distribution by Team</CardTitle>
                  <Select value={selectedMonth.toString()} onValueChange={(val) => setSelectedMonth(parseInt(val))}>
                    <SelectTrigger className="w-32 h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {monthNames.map((name, idx) => (
                        <SelectItem key={idx} value={idx.toString()}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie
                      data={teamDistributionPieData}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      label={({ name, percent }) => `${name}\n${(percent * 100).toFixed(1)}%`}
                      outerRadius={100}
                      dataKey="value"
                    >
                      {teamDistributionPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={TEAM_COLORS[index % TEAM_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* COST TAB */}
        <TabsContent value="cost" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Cost Component overview */}
            <Card className="shadow-sm border border-gray-200">
              <CardHeader className="border-b bg-gray-50">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-base font-semibold text-gray-900">
                    Cost Component overview (Amount in USD)
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Select value={costFormat} onValueChange={setCostFormat}>
                      <SelectTrigger className="w-32 h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {AMOUNT_FORMATS.map((fmt) => (
                          <SelectItem key={fmt.value} value={fmt.value}>{fmt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <ResponsiveContainer width="100%" height={320}>
                  <ComposedChart data={costTrendData} margin={{ top: 30, right: 10, bottom: 20, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                    <XAxis dataKey="month" stroke="#6B7280" tick={{ fontSize: 11 }} />
                    <YAxis 
                      stroke="#6B7280" 
                      tick={{ fontSize: 11 }}
                      domain={[0, (dataMax) => Math.ceil(dataMax * 1.15)]}
                      tickFormatter={(value) => formatYAxisCost(value, costFormat)}
                    />
                    <Tooltip content={<CustomTooltip isHeadcount={false} format={costFormat} />} />
                    <Legend iconType="square" wrapperStyle={{ fontSize: 11 }} />
                    <Bar 
                      dataKey="payroll" 
                      stackId="a" 
                      fill={COLORS.blue} 
                      name="Payroll" 
                      radius={[0, 0, 0, 0]} 
                    />
                    <Bar 
                      dataKey="taxes" 
                      stackId="a" 
                      fill={COLORS.red} 
                      name="Taxes" 
                      radius={[0, 0, 0, 0]} 
                    />
                    <Bar 
                      dataKey="benefits" 
                      stackId="a" 
                      fill={COLORS.green} 
                      name="Benefits" 
                      radius={[0, 0, 0, 0]} 
                    />
                    <Bar 
                      dataKey="bonus" 
                      stackId="a" 
                      fill={COLORS.yellow} 
                      name="Bonus" 
                      radius={[4, 4, 0, 0]} 
                      label={{ 
                        position: 'top', 
                        fontSize: 10,
                        formatter: (value) => formatYAxisCost(value, costFormat)
                      }} 
                    />
                    <Line 
                      type="monotone" 
                      dataKey="total" 
                      stroke={COLORS.orange} 
                      strokeWidth={2} 
                      name="Total Cost" 
                      dot={false} 
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Staff Cost - Components Pie */}
            <Card className="shadow-sm border border-gray-200">
              <CardHeader className="border-b bg-gray-50">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-base font-semibold text-gray-900">
                    Staff Cost - Components (FTE + Contractors)
                  </CardTitle>
                  <Select value={selectedMonth.toString()} onValueChange={(val) => setSelectedMonth(parseInt(val))}>
                    <SelectTrigger className="w-32 h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {monthNames.map((name, idx) => (
                        <SelectItem key={idx} value={idx.toString()}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie
                      data={costComponentsPieData}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      label={({ name, percent }) => `${name}\n${(percent * 100).toFixed(1)}%`}
                      outerRadius={100}
                      dataKey="value"
                    >
                      <Cell fill={COLORS.blue} />
                      <Cell fill={COLORS.red} />
                      <Cell fill={COLORS.green} />
                      <Cell fill={COLORS.yellow} />
                    </Pie>
                    <Tooltip formatter={(value) => formatAmount(value, 'actual', true)} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Cost by Team */}
            <Card className="shadow-sm border border-gray-200">
              <CardHeader className="border-b bg-gray-50">
                <CardTitle className="text-base font-semibold text-gray-900">
                  Cost by Team (Amount in USD)
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={teamCostData} margin={{ top: 30, right: 10, bottom: 20, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                    <XAxis dataKey="month" stroke="#6B7280" tick={{ fontSize: 11 }} />
                    <YAxis 
                      stroke="#6B7280" 
                      tick={{ fontSize: 11 }}
                      domain={[0, (dataMax) => Math.ceil(dataMax * 1.15)]}
                      tickFormatter={(value) => formatYAxisCost(value, costFormat)}
                    />
                    <Tooltip content={<CustomTooltip isHeadcount={false} format={costFormat} />} />
                    <Legend iconType="square" wrapperStyle={{ fontSize: 11 }} />
                    {teamKeys.map((team, idx) => (
                      <Bar
                        key={team}
                        dataKey={team}
                        stackId="a"
                        fill={TEAM_COLORS[idx % TEAM_COLORS.length]}
                        radius={idx === teamKeys.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Full-time vs Contractor */}
            <Card className="shadow-sm border border-gray-200">
              <CardHeader className="border-b bg-gray-50">
                <CardTitle className="text-base font-semibold text-gray-900">
                  Full-time vs Contractor (Amount in USD)
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={ftContractorCostData} margin={{ top: 30, right: 10, bottom: 20, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                    <XAxis dataKey="month" stroke="#6B7280" tick={{ fontSize: 11 }} />
                    <YAxis 
                      stroke="#6B7280" 
                      tick={{ fontSize: 11 }}
                      domain={[0, (dataMax) => Math.ceil(dataMax * 1.15)]}
                      tickFormatter={(value) => formatYAxisCost(value, costFormat)}
                    />
                    <Tooltip content={<CustomTooltip isHeadcount={false} format={costFormat} />} />
                    <Legend iconType="square" wrapperStyle={{ fontSize: 12 }} />
                    <Bar 
                      dataKey="fulltime" 
                      stackId="a" 
                      fill={COLORS.blue} 
                      name="Full-time" 
                      radius={[0, 0, 0, 0]} 
                      label={{ 
                        position: 'inside', 
                        fontSize: 10, 
                        fill: '#fff',
                        formatter: (value) => formatYAxisCost(value, costFormat)
                      }} 
                    />
                    <Bar 
                      dataKey="contractor" 
                      stackId="a" 
                      fill={COLORS.red} 
                      name="Contractor" 
                      radius={[4, 4, 0, 0]} 
                      label={{ 
                        position: 'inside', 
                        fontSize: 10, 
                        fill: '#fff',
                        formatter: (value) => formatYAxisCost(value, costFormat)
                      }} 
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default UltimateDashboard;
